/* =========================================================================
   The Wind Chime Co — Quiz Loader (premium, fast fade, back arrow)
   - Runs only on /pages/chime-quiz
   - Email capture before results (Shopify contact / marketing opt-in)
   - Stock-aware fallback: if primary OOS/draft → alt becomes primary → fallback becomes alt
   - Subtle gold accents (#b4935a)
   ======================================================================== */

document.addEventListener("DOMContentLoaded", () => {
  // Only run on the quiz page
  if (!/\/pages\/chime-quiz\/?$/.test(location.pathname)) return;

  // Reset any stale state on every load
  sessionStorage.removeItem("wccQuizState");

  // Basic style (lightweight, premium)
  injectStyles();

  const main = document.getElementById("MainContent") || document.body;
  main.innerHTML = `
    <section id="wcc-quiz" class="wcc-wrap">
      <div class="wcc-card">
        <div class="wcc-header">
          <h1 class="wcc-title">Find Your Perfect Chime</h1>
          <p class="wcc-sub">Answer a few quick questions and we’ll match a top pick and a beautiful alternative.</p>
        </div>

        <div class="wcc-body">
          <div class="wcc-step" id="wcc-step"></div>
        </div>

        <div class="wcc-footer">
          <button type="button" id="wcc-back" class="wcc-back" aria-label="Back" style="display:none;">←</button>
        </div>
      </div>
    </section>
  `;

  const stepEl = document.getElementById("wcc-step");
  const backBtn = document.getElementById("wcc-back");

  // ---- State machine ----------------------------------------------------
  const state = {
    occasion: null,        // "memorial" | "home" | "special" | "cause"
    specialType: null,     // "wedding" | "retirement" | "house"
    personalization: null, // "yes" | "no" (memorial only)
    bundle: null,          // "yes" | "no" (memorial only)
    tone: null,            // "gentle" | "warm" | "deep"
    email: null
  };

  const history = []; // track steps for back nav

  // ---- Step definitions -------------------------------------------------
  const STEPS = {
    occasion: {
      id: "occasion",
      heading: "What brings you here today?",
      options: [
        { label: "Remember a Loved One", value: "memorial" },
        { label: "To Bless or Beautify a Home", value: "home" },
        { label: "To Celebrate a Special Occasion", value: "special" },
        { label: "To Support a Cause", value: "cause" }
      ],
      onNext: (val) => {
        state.occasion = val;
        if (val === "memorial") return "personalization";
        if (val === "home")     return "tone";
        if (val === "special")  return "specialType";
        if (val === "cause")    return "email";
        return null;
      },
      showBack: false
    },

    specialType: {
      id: "specialType",
      heading: "What’s the special occasion?",
      options: [
        { label: "Wedding or Anniversary", value: "wedding" },
        { label: "Retirement", value: "retirement" },
        { label: "Housewarming or New Home", value: "house" }
      ],
      onNext: (val) => { state.specialType = val; return "tone"; },
      showBack: true
    },

    personalization: {
      id: "personalization",
      heading: "Would you like to personalize your chime?",
      options: [
        { label: "Yes, make it personal", value: "yes" },
        { label: "No, standard design is fine", value: "no" }
      ],
      onNext: (val) => { state.personalization = val; return "bundle"; },
      showBack: true
    },

    bundle: {
      id: "bundle",
      heading: "Would you like the premium gift bundle experience?",
      sub: "Includes our acrylic memorial heart and chime for a keepsake unboxing.",
      options: [
        { label: "Yes, premium bundle", value: "yes" },
        { label: "No, just the chime", value: "no" }
      ],
      onNext: (val) => { state.bundle = val; return "tone"; },
      showBack: true
    },

    tone: {
      id: "tone",
      heading: "What sound do you prefer?",
      options: [
        { label: "Gentle & Light", value: "gentle" },
        { label: "Warm & Full",   value: "warm" },
        { label: "Deep & Resonant", value: "deep" }
      ],
      onNext: (val) => { state.tone = val; return "email"; },
      showBack: true
    },

    email: {
      id: "email",
      heading: emailHeadingFor(state),
      sub: "Enter your email to see your personalized results (and get occasional VIP offers).",
      render: () => emailFormHTML(),
      bind: () => bindEmailCapture(finish),
      showBack: true
    }
  };

  // ---- Boot: always start at "occasion" -------------------------------
  goTo("occasion");

  // ---- Back button -----------------------------------------------------
  backBtn.addEventListener("click", () => {
    // remove current
    history.pop();
    const prev = history.pop() || "occasion";
    // reset dependent state
    if (prev === "occasion") {
      state.occasion = null; state.specialType = null; state.personalization = null; state.bundle = null; state.tone = null; state.email = null;
    } else if (prev === "specialType") {
      state.specialType = null; state.tone = null; state.email = null;
    } else if (prev === "personalization") {
      state.personalization = null; state.bundle = null; state.tone = null; state.email = null;
    } else if (prev === "bundle") {
      state.bundle = null; state.tone = null; state.email = null;
    } else if (prev === "tone") {
      state.tone = null; state.email = null;
    } else if (prev === "email") {
      state.email = null;
    }
    goTo(prev, false);
  });

  // ---- Functions: render/bind steps -----------------------------------
  function goTo(stepId, push = true) {
    const step = STEPS[stepId];
    if (!step) return;

    // back visibility
    backBtn.style.display = step.showBack ? "inline-block" : "none";

    fadeOut(stepEl, () => {
      stepEl.innerHTML = stepHTML(step);
      if (step.id !== "email") bindOptions(step);
      if (step.bind) step.bind();
      fadeIn(stepEl);
    });

    if (push) history.push(stepId);
  }

  function stepHTML(step) {
    const sub = step.sub ? `<p class="wcc-sub2">${step.sub}</p>` : "";
    if (step.render) return `
      <div class="wcc-q">
        <h2 class="wcc-h2">${step.heading}</h2>
        ${sub}
        ${step.render()}
      </div>
    `;
    // default options-based step
    return `
      <div class="wcc-q">
        <h2 class="wcc-h2">${step.heading}</h2>
        ${sub}
        <div class="wcc-btns">
          ${step.options.map(o => buttonHTML(o.label, o.value)).join("")}
        </div>
      </div>
    `;
  }

  function buttonHTML(label, value) {
    return `
      <button class="wcc-btn" data-value="${value}">
        ${label}
      </button>
    `;
  }

  function bindOptions(step) {
    const btnWrap = stepEl.querySelector(".wcc-btns");
    if (!btnWrap) return;
    btnWrap.addEventListener("click", (e) => {
      const btn = e.target.closest(".wcc-btn");
      if (!btn) return;
      // visual selection
      btnWrap.querySelectorAll(".wcc-btn").forEach(b => { b.classList.remove("selected"); });
      btn.classList.add("selected");

      // go next
      const nextId = step.onNext(btn.getAttribute("data-value"));
      if (nextId) goTo(nextId);
    });
  }

  function emailHeadingFor(st) {
    return (st.occasion === "cause")
      ? "Where should we send your chime pick?"
      : "One last step — where should we send your results?";
  }

  function emailFormHTML() {
    return `
      <form id="wcc-email-form" class="wcc-email">
        <input id="wcc-email" type="email" placeholder="Your email address" required />
        <button id="wcc-see" type="submit">See My Results</button>
        <div id="wcc-email-msg" class="wcc-msg"></div>
      </form>
    `;
  }

  function bindEmailCapture(cb) {
    const form = document.getElementById("wcc-email-form");
    const inp  = document.getElementById("wcc-email");
    const msg  = document.getElementById("wcc-email-msg");

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const email = (inp.value || "").trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        msg.textContent = "Please enter a valid email."; msg.style.color = "#E02229"; return;
      }
      msg.textContent = "Submitting…"; msg.style.color = "#555";

      // Create/update contact + marketing opt-in
      try {
        await fetch("/contact", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            form_type: "customer",
            utf8: "✓",
            "contact[email]": email,
            "contact[accepts_marketing]": "true",
            "contact[tags]": "quiz-optin"
          }).toString()
        });
      } catch(err) {
        console.warn("Email submit error (continuing to results):", err);
      }

      state.email = email;
      msg.textContent = "Success! Loading your results…"; msg.style.color = "green";
      setTimeout(cb, 350);
    });
  }

  // ---- Finish: compute handles + stock-aware fallback + redirect -------
  async function finish() {
    const { primary, alt, fallback } = computeHandles(state);

    // resolve availability
    const pProd = await fetchProduct(primary);
    const aProd = await fetchProduct(alt);
    const fProd = fallback ? await fetchProduct(fallback) : null;

    const pOK = isAvailable(pProd);
    const aOK = isAvailable(aProd);
    const fOK = isAvailable(fProd);

    let finalPrimary = primary;
    let finalAlt     = alt;

    // Stock-aware rule: if primary OOS/draft → alt becomes primary → fallback becomes alt (if available)
    if (!pOK) {
      if (aOK) {
        finalPrimary = alt;
        finalAlt     = fOK ? fallback : (pOK ? primary : alt); // if no fallback, leave alt as-is
      } else if (fOK) {
        finalPrimary = fallback;
        finalAlt     = aOK ? alt : primary;
      }
      // If nothing’s available, we’ll just try to proceed with what we have
    }

    const qp = new URLSearchParams({ primary: finalPrimary, alt: finalAlt }).toString();
    location.replace(`/pages/chime-results?${qp}`);
  }

  // ---- Product helpers --------------------------------------------------
  async function fetchProduct(handle) {
    if (!handle) return null;
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js?cb=${Date.now()}`, { credentials: "same-origin" });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }
  function isAvailable(prod) {
    if (!prod || !Array.isArray(prod.variants)) return false;
    return prod.variants.some(v => v.available);
  }

  // ---- Mapping: returns { primary, alt, fallback } ----------------------
  function computeHandles(st) {
    const o = st.occasion;      // memorial | home | special | cause
    const t = st.tone;          // gentle | warm | deep
    const b = st.bundle;        // yes | no
    const p = st.personalization; // yes | no
    const s = st.specialType;   // wedding | retirement | house

    // Handles provided by you (verbatim)
    const H = {
      // Memorial non-bundle
      mem32: "32-memorial-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections",
      mem30: "wedding-anniversary-wind-chime-whispering-hearts™-collection-platinum-rose-personalized-30", // 30 WH path (used as memorial 30 when needed)
      mem28: "28-wind-chime-with-gift-box-whispering-hearts™-collection-rose-gold-personalized",
      mem34: "34-family-love-memorial-wind-chimes-sympathy-wind-chime-gift-for-loss-of-a-loved-one",
      mem36: "36-serenity-wind-bell-thoughtful-memorial-gift-sympathy-gift-for-funerals",
      mem40: "untitled-aug5_21-44",
      mem48: "48-antique-bronze-flower",
      mem55: "large-deep-tone-memorial-wind-chimes-elegant-wind-chimes-for-loss-of-loved-one-55",

      // Memorial bundles
      bun28: "gift-set-28-wind-chime-with-gift-box-whispering-hearts™-collection-rose-gold-personalized",
      bun30: "gift-bundle-memorial-wind-chime-whispering-hearts™-collection-platinum-rose-personalized-30",
      bun32: "32-serenity-memorial-wind-chime-gift-set-thoughtful-gift-for-special-occasions-or-reflections",
      bun34: "34-serenity-family-love-memorial-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections-copy",
      bun40: "40-serenity-memorial-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections-amazing-grace-scripture-copy",
      bun48: "gift-bundle-memorial-serenity-wind-chime-antique-bronze-flower-for-special-occasions-or-reflections-48",
      bun55: "gift-bundle-memorial-serenity-large-deep-tone-wind-chime-church-bells-thoughtful-gift-for-special-occasions-or-reflections-55",

      // Wedding
      wed30: "wedding-anniversary-wind-chime-whispering-hearts™-collection-platinum-rose-personalized-30",
      wed32: "home-blessing-serenity-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections-32", // per your mapping earlier
      wed34: "wedding-anniversary-serenity-family-love-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections-34",
      wed36: "xl-wedding-anniversary-serenity-wind-bell-thoughtful-gift-for-special-occasions-or-reflections-36",
      wed48: "wedding-anniversary-serenity-wind-chime-antique-bronze-flower-for-special-occasions-or-reflections-49",
      wed55: "wedding-anniversary-large-serenity-deep-tone-wind-chime-church-bells-thoughtful-gift-for-special-occasions-or-reflections-55",

      // Retirement
      ret30: "retirement-wind-chime-whispering-hearts™-collection-platinum-rose-30",
      ret32: "retirement-serenity-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections-32",
      ret34: "retirement-serenity-family-love-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections-34",
      ret36: "xl-retirement-serenity-wind-bell-thoughtful-gift-for-special-occasions-or-reflections-36",
      ret48: "retirement-serenity-wind-chime-antique-bronze-flower-for-special-occasions-or-reflections-49",
      ret55: "retirement-large-serenity-deep-tone-wind-chime-church-bells-thoughtful-gift-for-special-occasions-or-reflections-55",

      // Home Blessing
      home30: "memorial-wind-chime-whispering-hearts™-collection-platinum-rose-personalized-30-copy",
      home32: "home-blessing-serenity-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections-33",
      home34: "home-blessing-serenity-family-love-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections-34",
      home36: "xl-serenity-home-blessing-wind-bell-thoughtful-gift-for-special-occasions-or-reflections-36",
      home40: "home-blessing-serenity-wind-chimes-thoughtful-gift-for-special-occasions-or-reflections-amazing-grace-scripture-40",
      home48: "home-blessing-serenity-wind-chime-antique-bronze-flower-for-special-occasions-or-reflections-49",
      home55: "home-blessing-large-serenity-deep-tone-wind-chime-church-bells-thoughtful-gift-for-special-occasions-or-reflections-55",

      // Cause
      cause29: "serenity-for-a-cause-breast-cancer-29"
    };

    // ----- MEMORIAL -----
    if (o === "memorial") {
      const bundled = (b === "yes");
      const personalized = (p === "yes");

      if (!bundled) {
        if (t === "gentle") {
          // Personalized: 32 then 30. Not personalized: 32 then 28
          return personalized
            ? trio(H.mem32, H.mem30, H.mem34)
            : trio(H.mem32, H.mem28, H.mem30);
        } else if (t === "warm") {
          // Personalized: 40 then 34. Not personalized: 30 then 40
          return personalized
            ? trio(H.mem40, H.mem34, H.mem32)
            : trio(H.mem30, H.mem40, H.mem34);
        } else { // deep
          // Personalized: 48 then 55. Not personalized: 55 then 48
          return personalized
            ? trio(H.mem48, H.mem55, H.mem36)
            : trio(H.mem55, H.mem48, H.mem36);
        }
      } else {
        // Bundled
        if (t === "gentle") {
          // Personalized: 32 bundle then 34 bundle. Not personalized: 32 bundle then 28 bundle
          return personalized
            ? trio(H.bun32, H.bun34, H.bun30)
            : trio(H.bun32, H.bun28, H.bun34);
        } else if (t === "warm") {
          // Personalized: 40 bundle then 30 bundle. Not personalized: 30 bundle then 40 bundle
          return personalized
            ? trio(H.bun40, H.bun30, H.bun32)
            : trio(H.bun30, H.bun40, H.bun32);
        } else { // deep
          // Personalized: 48 bundle then 55 bundle. Not personalized: 55 bundle then 48 bundle
          return personalized
            ? trio(H.bun48, H.bun55, H.bun40)
            : trio(H.bun55, H.bun48, H.bun40);
        }
      }
    }

    // ----- HOME BLESSING -----
    if (o === "home") {
      if (t === "gentle") return trio(H.home32, H.home34, H.home30);
      if (t === "warm")   return trio(H.home34, H.home40, H.home32);
      // deep
      return trio(H.home48, H.home55, H.home36);
    }

    // ----- SPECIAL OCCASION -----
    if (o === "special") {
      if (s === "wedding") {
        if (t === "gentle") return trio(H.wed32, H.wed30, H.wed34);
        if (t === "warm")   return trio(H.wed30, H.wed34, H.wed32);
        return trio(H.wed48, H.wed55, H.wed36); // deep
      } else if (s === "retirement") {
        if (t === "gentle") return trio(H.ret32, H.ret30, H.ret34);
        if (t === "warm")   return trio(H.ret30, H.ret34, H.ret32);
        return trio(H.ret48, H.ret55, H.ret36);
      } else { // housewarming/new home
        if (t === "gentle") return trio(H.home32, H.home34, H.home30);
        if (t === "warm")   return trio(H.home34, H.home40, H.home32);
        return trio(H.home48, H.home55, H.home36);
      }
    }

    // ----- FOR A CAUSE -----
    if (o === "cause") {
      // primary = breast cancer 29, alt = memorial 32, fallback = home 32
      return trio(H.cause29, H.mem32, H.home32);
    }

    // Fallback (shouldn’t happen)
    return trio(H.mem32, H.mem34, H.mem30);
  }

  function trio(primary, alt, fallback) {
    return { primary, alt, fallback };
  }

  // ---- Animations / Styles ---------------------------------------------
  function fadeIn(el)  { requestAnimationFrame(() => { el.style.opacity = "1"; el.style.transform = "translateY(0)"; }); }
  function fadeOut(el, cb) { el.style.opacity = "0"; el.style.transform = "translateY(10px)"; setTimeout(cb, 300); }

  function injectStyles() {
    const css = `
      .wcc-wrap { max-width: 760px; margin: 56px auto 90px; padding: 0 18px; }
      .wcc-card { background:#fff; border:1px solid #e6e6e6; border-radius:14px; box-shadow:0 6px 18px rgba(0,0,0,.06); overflow:hidden; }
      .wcc-header { padding: 26px 24px 10px; text-align:center; }
      .wcc-title { margin:0 0 6px; font-weight:800; }
      .wcc-sub { margin:0; color:#444; }
      .wcc-body { padding: 22px 24px 10px; }
      .wcc-footer { display:flex; justify-content:flex-start; align-items:center; padding: 6px 16px 16px; }

      #wcc-step { opacity:0; transform:translateY(10px); transition: all .3s ease; }
      .wcc-h2 { font-size: 1.35rem; margin: 6px 0 14px; font-weight: 750; }
      .wcc-sub2 { margin: -2px 0 14px; color:#555; }

      .wcc-btns { display:flex; flex-direction:column; gap:12px; margin-top:4px; }
      .wcc-btn {
        width:100%; max-width:560px; border:2px solid #000; background:#fff; color:#000;
        border-radius:10px; padding:14px 16px; font-size:1rem; font-weight:700; letter-spacing:.2px;
        cursor:pointer; transition: background .18s ease, color .18s ease, transform .18s ease, box-shadow .18s ease;
      }
      .wcc-btn:hover { box-shadow:0 0 0 2px #000 inset; }
      .wcc-btn.selected { background:#000; color:#fff; }

      .wcc-back {
        border:none; background:transparent; color:#000; font-size:20px; line-height:1;
        padding: 8px 10px; border-radius:8px; cursor:pointer; transition: color .15s ease, transform .15s ease;
      }
      .wcc-back:hover { color:#b4935a; transform: translateX(-1px); }

      .wcc-email { display:flex; flex-direction:column; gap:12px; max-width:560px; }
      .wcc-email input {
        width:100%; border:2px solid #000; border-radius:10px; padding:14px 16px; font-size:1rem;
      }
      .wcc-email button {
        width:100%; border:2px solid #000; background:#000; color:#fff; border-radius:10px;
        padding:14px 16px; font-size:1rem; font-weight:800; letter-spacing:.2px; cursor:pointer;
        transition: background .18s ease, color .18s ease, transform .18s ease, box-shadow .18s ease;
      }
      .wcc-email button:hover { background:#333; }
      .wcc-msg { font-size: .95rem; color:#555; }
      @media (max-width: 480px) {
        .wcc-wrap { margin-top: 32px; }
      }
    `;
    const tag = document.createElement("style");
    tag.textContent = css;
    document.head.appendChild(tag);
  }
});